import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useCreateNotificationMutation } from 'src/reduxServer/services/notifications/index';
import PageButton from 'src/components/PageButton';
import FormInput from 'src/components/FormFields/FormFields';
import { showToast } from 'src/utils/toastfy';
import { useTranslation } from 'react-i18next';
import PageTypography from 'src/components/PageTypography/PageTypography';

interface NotificationFormValues {
  title_en: string;
  title_ar: string;
  description_en: string;
  action_link: string;
}

interface Props {
  openDialog: boolean;
  setOpenDialog: (open: boolean) => void;
}

const NewNotificationDialog = ({ openDialog, setOpenDialog }: Props) => {
  const { t } = useTranslation();
  const { control, handleSubmit, reset } = useForm<NotificationFormValues>({
    defaultValues: {
      title_en: '',
      title_ar: '',
      description_en: '',
      action_link: ''
    }
  });

  const [createNotification, { isLoading }] = useCreateNotificationMutation();

  const handleCloseDialog = () => {
    reset();
    setOpenDialog(false);
  };

  const onSubmit = async (data: NotificationFormValues) => {
    try {
      const result = await createNotification({
        ...data,
        description_ar: data.description_en,
        is_read: false,
        type: 'system'
      }).unwrap();
      showToast(
        'success',
        result.message || t('Notification created successfully')
      );
      handleCloseDialog();
    } catch (error) {
      console.error(t('Create notification failed'), error);
    }
  };

  return (
    <Dialog
      open={openDialog}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1 } }}
    >
      <DialogTitle>
        <PageTypography fontWeight={700}>
          {t('New Notification')}
        </PageTypography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormInput
              name="title_en"
              label={t('Title (EN)')}
              control={control}
              rules={{ required: t('Title (EN) is required') }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormInput
              name="title_ar"
              label={t('Title (AR)')}
              control={control}
              rules={{ required: t('Title (AR) is required') }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormInput
              name="description_en"
              label={t('Description (EN)')}
              control={control}
              rules={{ required: t('Description is required') }}
              multiline
              rows={3}
            />
          </Grid>

          <Grid item xs={12}>
            <FormInput
              name="description_ar"
              label={t('description_ar')}
              control={control}
              rules={{ required: t('description_ar is required') }}
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
      >
        <PageButton
          variant="outlined"
          onClick={handleCloseDialog}
          sx={{ marginInlineEnd: 1 }}
        >
          {t('Cancel')}
        </PageButton>

        <PageButton
          variant="contained"
          loading={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {t('Add')}
        </PageButton>
      </DialogActions>
    </Dialog>
  );
};

export default NewNotificationDialog;
